import { FreeCamera, Quaternion, Scene, Vector3 } from '@babylonjs/core';
import { DIAGNOSTIC } from '../engine/Diagnostic.js';
import type { VisualSettings } from '../engine/Settings.js';

const POSITION_SMOOTH = 6.0; // 1/sec — base rate
const TARGET_SMOOTH = 12.0;
const MAX_FOV_BOOST = 0.18;
const FOV_SPEED_REF = 35; // m/s where the speed-FoV boost saturates

// Distance reference proportions relative to settings.cameraDistance (the user-tunable
// baseline). Numbers chosen so default distance=7 reproduces the prior fixed values
// 5 / 7 / 11 / 6.
const STANDSTILL_RATIO = 5 / 7;
const MAX_ACCEL_RATIO = 11 / 7;
const DECEL_RATIO = 6 / 7;
const ACCEL_REF = 5.0; // m/s² where distance pull saturates
const STANDSTILL_SPEED_THRESHOLD = 0.5;
// Acceleration-pullback only meaningful at low speed — the goal is "flat-start feels
// fast", not "camera keeps pulling back while cruising". Decel keeps working at any speed.
const ACCEL_GATE_FULL_KMH = 30; // full pullback effect at/below this speed
const ACCEL_GATE_ZERO_KMH = 40; // pullback fades to zero by this speed

// Camera lag.
const LAG_SLOWDOWN_FACTOR = 0.35; // when accelerating, position-smoothing drops to this factor
const MAX_LAG = 2.0; // hard cap on extra meters camera can fall behind while accelerating

// Distance LPF rate when accel-based distance changes the target (1/sec).
const DISTANCE_SMOOTH = 3.0;

// Camera shake. Triggered exclusively by collisions (via triggerShake() from the
// vehicle's impact-impulse path). No acceleration-based feed — heavy braking and hard
// launches don't shake the camera by themselves.
const SHAKE_DECAY = 10.0; // exponential decay rate (1/sec) — half-life ≈ 70ms
const SHAKE_AMP_POS = 0.08; // meters
const SHAKE_AMP_TARGET = 0.012; // ~rad equivalent applied to look-target offset

export class ChaseCamera {
  readonly camera: FreeCamera;
  private settings: VisualSettings;
  private currentPos: Vector3;
  private currentTarget: Vector3;
  private currentDistance: number;
  private prevForwardSpeed = 0;
  private smoothedAccel = 0;
  private shakeEnergy = 0;
  private shakeClock = 0;

  constructor(scene: Scene, settings: VisualSettings) {
    this.settings = settings;
    this.camera = new FreeCamera('chase', new Vector3(0, 10, -10), scene);
    this.camera.fov = settings.fovDegrees * (Math.PI / 180);
    this.camera.minZ = 0.2;
    // 6000m so visible geometry (fog slider can push terrain visibility to ~3500m, and
    // the cached chunk ring extends ~4500m) stays well inside the far plane. With maxZ
    // anywhere near actual geometry distance, depth precision clusters so tightly at
    // z=1.0 that distant terrain ties with the skybox (also at NDC z=1.0) and the
    // skybox wins the LEQUAL test, eating silhouettes on the horizon when Sky is on.
    this.camera.maxZ = 6000;
    this.camera.rotationQuaternion = Quaternion.Identity();
    this.currentPos = this.camera.position.clone();
    this.currentTarget = new Vector3();
    this.currentDistance = settings.cameraDistance;
  }

  attachCanvas(canvas: HTMLCanvasElement): void {
    void canvas;
  }

  /** Bump shake energy externally (used for crash-style impulses). */
  triggerShake(magnitude: number): void {
    if (!this.settings.cameraShakeEnabled || magnitude <= 0) return;
    this.shakeEnergy = Math.min(2.0, this.shakeEnergy + magnitude);
  }

  /**
   * @param targetPos world position of the vehicle
   * @param forward unit vector pointing the way the vehicle is facing
   * @param forwardSpeed scalar forward speed in m/s (signed: + forward, - reverse)
   * @param dt seconds since last frame
   */
  update(targetPos: Vector3, forward: Vector3, forwardSpeed: number, dt: number): void {
    if (dt <= 0) return;

    // ─── DIAGNOSTIC: bypass all camera mods, mirror baseline behavior ────────
    if (!DIAGNOSTIC.ENABLE_CAMERA_MODS) {
      const desiredPos = targetPos
        .add(forward.scale(-7.5))
        .add(new Vector3(0, 3.0, 0));
      const desiredTarget = targetPos.add(new Vector3(0, 1.0, 0)).add(forward.scale(2));
      if (DIAGNOSTIC.BUMP_EXAGGERATE) {
        // No smoothing at all — camera snaps instantly, every bump is maximally visible.
        this.currentPos.copyFrom(desiredPos);
        this.currentTarget.copyFrom(desiredTarget);
      } else {
        const tPos = 1 - Math.exp(-6 * dt);
        const tTgt = 1 - Math.exp(-12 * dt);
        this.currentPos = Vector3.Lerp(this.currentPos, desiredPos, tPos);
        this.currentTarget = Vector3.Lerp(this.currentTarget, desiredTarget, tTgt);
      }
      this.camera.position.copyFrom(this.currentPos);
      this.camera.setTarget(this.currentTarget);
      this.camera.fov = 0.9 + 0.18 * Math.min(1, Math.abs(forwardSpeed) / 35);
      return;
    }
    // ─────────────────────────────────────────────────────────────────────────

    // Smoothed forward acceleration (m/s²). Low-pass to absorb single-frame spikes.
    const rawAccel = (forwardSpeed - this.prevForwardSpeed) / dt;
    this.prevForwardSpeed = forwardSpeed;
    const accelLpf = 1 - Math.exp(-6 * dt);
    this.smoothedAccel = this.smoothedAccel + (rawAccel - this.smoothedAccel) * accelLpf;

    // Distance from settings + accel envelope.
    const baseDistance = this.settings.cameraDistance;
    const standstillDistance = baseDistance * STANDSTILL_RATIO;
    const maxAccelDistance = baseDistance * MAX_ACCEL_RATIO;
    const decelDistance = baseDistance * DECEL_RATIO;

    let accelDistance = baseDistance;
    if (Math.abs(forwardSpeed) < STANDSTILL_SPEED_THRESHOLD && Math.abs(this.smoothedAccel) < 1.0) {
      accelDistance = standstillDistance;
    } else if (this.smoothedAccel >= 0) {
      // Accel pullback gated by speed — full effect below 30 km/h, fades to nothing by
      // 40 km/h. So a 0-to-fast launch from a stop pulls the camera back, but the camera
      // doesn't keep drifting away while cruising.
      const speedKmh = Math.abs(forwardSpeed) * 3.6;
      const speedGate = 1 - smoothstep(ACCEL_GATE_FULL_KMH, ACCEL_GATE_ZERO_KMH, speedKmh);
      const t = smoothstep(0, ACCEL_REF, this.smoothedAccel) * speedGate;
      accelDistance = lerp(baseDistance, maxAccelDistance, t);
    } else {
      // Deceleration: always active so hard braking at any speed pulls the camera in.
      const t = smoothstep(0, ACCEL_REF, -this.smoothedAccel);
      accelDistance = lerp(baseDistance, decelDistance, t);
    }

    // Scale the accel-driven variation by the user's amount (0 = no variation,
    // 1 = full effect, 2 = exaggerated).
    const targetDistance = lerp(baseDistance, accelDistance, this.settings.accelDistanceAmount);

    const distLpf = 1 - Math.exp(-DISTANCE_SMOOTH * dt);
    this.currentDistance = this.currentDistance + (targetDistance - this.currentDistance) * distLpf;

    const desiredPos = targetPos
      .add(forward.scale(-this.currentDistance))
      .add(new Vector3(0, this.settings.cameraHeight, 0));
    const desiredTarget = targetPos.add(new Vector3(0, 1.0, 0)).add(forward.scale(2));

    // Position smoothing — slowed during forward acceleration to produce visible lag.
    let posRate = POSITION_SMOOTH;
    if (this.settings.cameraLagEnabled && this.smoothedAccel > 0) {
      const accelFactor = clamp01(this.smoothedAccel / ACCEL_REF);
      posRate = lerp(POSITION_SMOOTH, POSITION_SMOOTH * LAG_SLOWDOWN_FACTOR, accelFactor);
    }
    const tPos = 1 - Math.exp(-posRate * dt);
    const tTgt = 1 - Math.exp(-TARGET_SMOOTH * dt);
    this.currentPos = Vector3.Lerp(this.currentPos, desiredPos, tPos);
    this.currentTarget = Vector3.Lerp(this.currentTarget, desiredTarget, tTgt);

    // Bound the lag: don't let the camera drift further than currentDistance + cameraMaxLag
    // away from the car.
    if (this.settings.cameraLagEnabled) {
      const carToCam = this.currentPos.subtract(targetPos);
      const dist = carToCam.length();
      const limit = this.currentDistance + MAX_LAG;
      if (dist > limit) {
        const scaled = carToCam.scale(limit / dist);
        this.currentPos = targetPos.add(scaled);
      }
    }

    // Shake decays continuously; energy is only ever added via triggerShake() (collisions).
    if (this.settings.cameraShakeEnabled) {
      this.shakeEnergy *= Math.exp(-SHAKE_DECAY * dt);
      this.shakeClock += dt;
    } else {
      this.shakeEnergy = 0;
    }

    // Apply shake offset to position + look-target.
    let camPos = this.currentPos;
    let camTarget = this.currentTarget;
    if (this.shakeEnergy > 0.001) {
      const intensity = this.shakeEnergy * this.settings.cameraShakeIntensity;
      const t = this.shakeClock;
      const sx = (0.4 * Math.sin(t * 23) + 0.6 * Math.sin(t * 37) + 0.5 * Math.sin(t * 53)) / 1.5;
      const sy = (0.4 * Math.sin(t * 29) + 0.6 * Math.sin(t * 41) + 0.5 * Math.sin(t * 59)) / 1.5;
      const sz = (0.4 * Math.sin(t * 31) + 0.6 * Math.sin(t * 43) + 0.5 * Math.sin(t * 61)) / 1.5;
      camPos = camPos.add(new Vector3(sx, sy, sz).scale(SHAKE_AMP_POS * intensity));
      camTarget = camTarget.add(new Vector3(sy, sx, sz).scale(SHAKE_AMP_TARGET * intensity));
    }

    this.camera.position.copyFrom(camPos);
    this.camera.setTarget(camTarget);

    // FoV: base from settings, optional speed boost on top.
    const baseFov = this.settings.fovDegrees * (Math.PI / 180);
    const boost = this.settings.speedFovBoost
      ? MAX_FOV_BOOST * Math.min(1, Math.abs(forwardSpeed) / FOV_SPEED_REF)
      : 0;
    this.camera.fov = baseFov + boost;
  }

  /** Apply a new baseline distance immediately (e.g. FoV-linked slider drag). */
  syncBaselineDistance(distance: number): void {
    this.currentDistance = distance;
  }

  snap(targetPos: Vector3, forward: Vector3): void {
    const base = this.settings.cameraDistance;
    const snapDist = lerp(base, base * STANDSTILL_RATIO, this.settings.accelDistanceAmount);
    this.currentDistance = snapDist;
    this.prevForwardSpeed = 0;
    this.smoothedAccel = 0;
    this.shakeEnergy = 0;
    const desiredPos = targetPos
      .add(forward.scale(-snapDist))
      .add(new Vector3(0, this.settings.cameraHeight, 0));
    const desiredTarget = targetPos.add(new Vector3(0, 1.0, 0)).add(forward.scale(2));
    this.currentPos.copyFrom(desiredPos);
    this.currentTarget.copyFrom(desiredTarget);
    this.camera.position.copyFrom(desiredPos);
    this.camera.setTarget(desiredTarget);
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}
